import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Media } from '@klickdata/core/media/src/media.model';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { AppScope, ResourceTag, ResourceTypes } from '@klickdata/core/resource';
import { DialogDisplayImgComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-display-img/dialog-display-img.component';
import { MatTableBase } from 'apps/klickdata/src/app/shared/table/mat-table-base';
import { filter, map, Observable, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { K3MediaListingService } from '../k3-media-listing.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageDeletedComponent, MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { TableFilterComponent } from '@klickdata/core/table';
import { User } from '@klickdata/core/user';
import { AuthService } from '@klickdata/core/auth';
import { SelectFilterOption } from '@klickdata/core/table';
import { Utils } from '@klickdata/core/util';

@Component({
    selector: 'app-media-listing-general',
    templateUrl: './media-listing-general.component.html',
    styleUrls: ['./media-listing-general.component.scss'],
    providers: [K3MediaListingService],
})
export class MediaListingGeneralComponent extends MatTableBase<Media> implements AfterViewInit {
    @Output() onMediaClick: EventEmitter<Media> = new EventEmitter<Media>();
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    @Input() public columns = [
        'id',
        'thumbnil',
        'filename',
        'description',
        'tags',
        'visibility',
        'priority',
        'author',
        'updated_at',
        'tools',
    ];
    public significantPrio = ['a1', 'a2', 'a3', 'b1', 'c3'];
    @Input() public hasExpansionFilter = true;
    AppScope = AppScope;
    public authUser: User;
    public filePrioOptions: SelectFilterOption[];

    constructor(
        protected listService: K3MediaListingService,
        protected mobileService: MobileService,
        protected cdr: ChangeDetectorRef,
        protected dialog: MatDialog,
        protected route: ActivatedRoute,
        protected router: Router,
        protected message: MessageService,
        protected auth: AuthService
    ) {
        super(cdr, mobileService, listService);
        this.filePrioOptions = Utils.getTaskPrioOptions();
        this.auth
            .getUser()
            .pipe(takeUntil(this.destroy))
            .subscribe((user) => {
                this.authUser = user;
            });
    }
    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.route.queryParams.pipe(filter((params) => params.action === 'addImage')).subscribe(() =>
            setTimeout(() => {
                this.addMedia();
                this.router.navigate([this.router.url.split('?')[0]]);
            }, 500)
        );
    }
    public refreshMediaListing() {
        this.dataSource.refresh();
    }
    public onFilterByFavorite(filter: boolean) {
        if (filter) {
            this.filter.createOrUpdateFilter([{ property: 'favorite', items: [this.authUser.id] }]);
        } else {
            this.filter.ignoreFilter(['favorite']);
        }
    }
    public addMedia() {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_IMAGE_TO_LIST,
            data: {
                type: 'addImage',
                mediaType: $localize`Image`,
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'addImage'),
                takeUntil(this.destroy),
                map((data) => data.value)
            )
            .subscribe((medias) => {
                this.dataSource.replace(medias.map((media) => new Media(media)));
                this.cdr.markForCheck();
            });
    }
    public createMaterial(media: Media) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.CREATE_RESOURCE,
            data: {
                typeId: ResourceTypes.DocMaterial,
                items: [{ name: media.path, item_type_value: media.type }],
            },
        });
    }
    public edit(media: Media) {
        this.openImageEditor(media).subscribe(() => this.dataSource.refresh());
    }
    public favorite(media: Media) {
        media.favorite = !media.favorite;
        this.listService
            .updateMedia(+media.id, { favorite: media.favorite }, 'tags,user_feeds')
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                this.dataSource.refresh();
                this.message.openMessage(MessageSavedComponent, $localize`Favorite updated`);
            });
    }

    public delete(id: number) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete Image`,
                contentBody: $localize`You are about to delete this image. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap((res) => {
                    if (res) {
                        return this.listService.destroy(id);
                    }
                })
            )
            .subscribe((action) => {
                if (action) {
                    this.message.openMessage(MessageDeletedComponent);
                    this.dataSource.refresh();
                }
            });
    }
    public view(media: Media) {
        this.dialog
            .open(DialogDisplayImgComponent, {
                maxWidth: this.isMobile ? '90%' : '70%',
                maxHeight: '90vh',
                data: media,
            })
            .afterClosed()
            .pipe(
                switchMap((result) => (result?.type === 'edit' ? this.openImageEditor(result?.media) : of(result))),
                takeUntil(this.destroy)
            )
            .subscribe(() => this.refreshMediaListing());
    }
    onTagClick(tag: ResourceTag | any) {
        this.filter.filterByModel(new ResourceTag(tag).getFilter(), true);
    }
    public updateMediaTags(media: Media, tagsIds: number[]) {
        this.listService.updateMedia(+media.id, { tag_ids: tagsIds }, 'tags').pipe(takeUntil(this.destroy)).subscribe();
    }
    onSelectPriority(media: Media, prio: string) {
        this.listService
            .updateMedia(+media.id, { priority: prio })
            .pipe(
                takeUntil(this.destroy),
                tap(() => this.dataSource.refresh())
            )
            .subscribe();
    }
    private openImageEditor(media?: Media): Observable<unknown> {
        if (!media) {
            return of(null);
        }
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_IMAGE_TO_LIST,
            data: {
                type: 'editImage',
                mediaType: $localize`Image`,
                media,
            },
        });
        return this.mobileService.getSideNavAction().pipe(
            filter((action) => action === SideNaveActionsTypes.POSITIVE),
            switchMap(() => this.mobileService.getSideNavResponseData()),
            filter((data) => data.type === 'editImage'),
            takeUntil(this.destroy)
        );
    }
}
